/**
 * @auth 赖传峰
 * @date 2022/3/31
 */
import { highlightFilesInDriveLink } from '@/components/share/share.script'
import _ from 'lodash'
import { type _GettersTree, defineStore } from 'pinia'
import { getShare, getShareDetail, type GetShareDetailQuery, type GetShareQuery } from '~/api'
import useDisplayMode from '~/composables/useDisplayMode'
import { FileOrder, RoutePathOfDrive, ShareStatus } from '~/types/enum'
import { isNaturalSort, naturalSort, type OrderType } from './share.script'

export const enum RestoreResultStatus {
  UNKNOWN = 'UNKNOWN',
  SUCCEED = 'SUCCEED',
  FAILED = 'FAILED',
}

export interface ShareState {
  data: Share
  restoreResult: {
    status: RestoreResultStatus
    parentId?: string
    fileIds?: string[]
    message?: string // 服务器返回的错误信息
  }
  dirs?: {
    id: string
    name: string
  }[]
  restoreCache: {
    parentId?: string
    fileIds?: string[]
    savedIds?: string[]
    barMessage?: string
  }
  mobileSelectMode: boolean
  detail: { [parentId: string]: ShareDetail | null }
  fileSpaceNotEnoughVisible: boolean // 文件空间不足提示
  tourGuideVisible: boolean // 下载指引 - 引导弹窗
  selectedIds: string[]
  tempOrder?: OrderType // 临时排序用，用于二级分享页满足特定条件时进行本地文件列表排序
  updatePremiumCallback?: () => void // 通过引导支付弹出购买会员成功后的回调
  urlQuery?: { [key: string]: string }
}

interface ShareGetters extends _GettersTree<ShareState> {
  paramOrder(state: ShareState): Exclude<FileOrder, FileOrder.NAME_ASC | FileOrder.NAME_DESC>
}

interface ShareActions {
  setShare(query: GetShareQuery): ReturnType<typeof getShare>

  setShareDetail(query: GetShareDetailQuery): ApiActionReturn

  setSelectedIds(ids: string[], toggle?: boolean): void

  gotoHighlightFile(): void
}

export const useShare = defineStore<'share', ShareState, ShareGetters, ShareActions>('share', {
  state: () => ({
    data: {
      share_status: ShareStatus.NOT_FOUND,
      share_status_text: '',
      expiration_left_seconds: '',
      expiration_left: '',
      file_num: '',
      files: [],
      next_page_token: '',
      pass_code_token: '',
      restore_count_left: '',
      title: '',
      user_info: null,
    },
    dirs: [],
    restoreResult: {
      status: RestoreResultStatus.UNKNOWN,
      parentId: undefined,
      fileIds: undefined,
      message: undefined,
    },
    restoreCache: {
      parentId: undefined,
      fileIds: undefined,
      savedIds: undefined,
      barMessage: undefined,
    },
    // 移动端，如果是选择模式，所有文件的单击变成选中
    mobileSelectMode: false,
    detail: {},
    fileSpaceNotEnoughVisible: false,
    tourGuideVisible: false, // 下载指引 - 引导弹窗的开关
    selectedIds: [],
    tempOrder: undefined,
    updatePremiumCallback: undefined,
    urlQuery: {},
  }),
  getters: {
    paramOrder() {
      const { orderType } = useDisplayMode()
      return (isNaturalSort(orderType.value) ? FileOrder.TYPE_DESC : orderType.value) as Exclude<
        FileOrder,
        FileOrder.NAME_ASC | FileOrder.NAME_DESC
      >
    },
  },
  actions: {
    async setShare(query) {
      const { orderType, pinFoldersTop } = useDisplayMode()
      const { data, error } = await getShare({
        order: this.paramOrder,
        folders_first: pinFoldersTop.value,
        ...query,
      })

      if (data.value.share_status === 'OK') {
        if (isNaturalSort(orderType.value)) {
          data.value.files = naturalSort(data.value?.files || [], orderType.value)
        }

        // url 的参数优先级更高
        if (Object.keys(this.urlQuery ?? {}).length) {
          data.value.params = {
            ...this.urlQuery,
          }
        }

        this.data = data.value
      }

      return { data, error }
    },
    async setShareDetail(query) {
      const { pinFoldersTop, orderType } = useDisplayMode()
      const detail = this.detail[query.parent_id] as ShareState['detail'][string]

      const { data, error } = await getShareDetail({
        order: this.paramOrder,
        folders_first: pinFoldersTop.value,
        ...query,
      })

      if (error.value) return { error: error.value }

      if (data.value.share_status === ShareStatus.OK) {
        const list = query.page_token
          ? {
              next_page_token: data.value.next_page_token,
              files: detail?.files.concat(data.value.files),
            }
          : data.value

        if (isNaturalSort(orderType.value)) {
          list.files = naturalSort(list?.files || [], orderType.value)
        }

        this.detail[query.parent_id] = list as ShareState['detail'][string]
      } else {
        this.$patch({
          data: {
            share_status: data.value.share_status,
            share_status_text: data.value.share_status_text,
          },
        })
      }

      return { error: null }
    },
    setSelectedIds(ids, toggle) {
      if (toggle) {
        return (this.selectedIds = _.xor([...this.selectedIds], ids))
      }
      this.selectedIds = [...new Set(ids)]
    },
    /** 跳转到云盘高亮文件 */
    gotoHighlightFile() {
      if (this.restoreCache.savedIds) {
        const fileIds = this.restoreCache.fileIds || []

        if (fileIds.length) {
          window.location.href = highlightFilesInDriveLink(fileIds)
        } else {
          window.location.href = RoutePathOfDrive.RECENT
        }
      }
    },
  },
})
